import {
  accentVariables, ACCENTS, CUSTOM_ACCENT, DEFAULT_ACCENT, DEFAULT_MODE, MODES,
} from './presets'
import showAccentImages from './accentImages'

/**
 * Puts a theme on the page: the accent's custom properties go inline on
 * <html>, the mode goes on <html data-mode>, and Vuexy's dark stylesheet is
 * switched with `body.dark-layout`.
 *
 * This runs once at boot (src/theme/index.js) and on every change, and nowhere
 * else. It replaced a global mixin that redid the whole colour derivation, and
 * wrote it all to localStorage, in the `created` hook of every component.
 */

// The light / dark choice, per device. public/index.html reads the same key
// before any CSS loads, so keep the two in sync.
export const STORAGE_KEY = 'cd-theme'

// Each community's accent as last seen on this device, so a reload paints it
// straight away. The server's copy (profile/appearance) is the one that
// counts: the accent belongs to the community, not to the device.
export const ACCENT_CACHE_KEY = 'cd-community-accents'

// Fired on window after a theme is applied. Canvas charts listen for it, since
// they paint colours once and cannot follow a CSS variable.
export const THEME_CHANGED_EVENT = 'cd:theme-changed'

export const isDarkModeEnabled = process.env.VUE_APP_FEATURE_DARK_MODE === 'true'

const darkQuery = () => window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)')

/** 'light' or 'dark': what `mode` means right now. */
export function resolveMode(mode) {
  if (!isDarkModeEnabled) return 'light'
  if (mode === 'system') {
    const query = darkQuery()
    return query && query.matches ? 'dark' : 'light'
  }
  return mode === 'dark' ? 'dark' : 'light'
}

// The old global mixin wrote these two keys on every page load; nothing reads
// them any more.
function removeLegacyKeys() {
  localStorage.removeItem('primary')
  localStorage.removeItem('secondary')
}

const readJson = key => {
  try {
    return JSON.parse(localStorage.getItem(key))
  } catch (e) {
    return null
  }
}

const writeJson = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch (e) {
    // Storage full or blocked: the theme still applies for this visit.
  }
}

export function saveMode(mode) {
  writeJson(STORAGE_KEY, { mode })
}

// The community this dashboard is for: the account the API token belongs to.
export function currentCommunityId() {
  const user = readJson('userData')
  return user && user.id ? String(user.id) : null
}

/**
 * An accent as the API sends it ({ accent, color }) or as the theme keeps it
 * ({ accent, custom }), made safe to apply: an unknown preset or a malformed
 * colour falls back to the default.
 */
export function normaliseAccent({ accent, custom, color } = {}) {
  if (accent === CUSTOM_ACCENT) {
    const primary = (custom && custom.primary) || color
    if (/^#[0-9a-fA-F]{6}$/.test(primary || '')) return { accent: CUSTOM_ACCENT, custom: { primary: primary.toLowerCase() } }
  }
  if (ACCENTS[accent]) return { accent, custom: null }
  return { accent: DEFAULT_ACCENT, custom: null }
}

export function readCachedAccent(communityId = currentCommunityId()) {
  const cache = readJson(ACCENT_CACHE_KEY) || {}
  return normaliseAccent((communityId && cache[communityId]) || {})
}

export function cacheAccent({ accent, custom }, communityId = currentCommunityId()) {
  if (!communityId) return
  const cache = readJson(ACCENT_CACHE_KEY) || {}
  cache[communityId] = { accent, custom }
  writeJson(ACCENT_CACHE_KEY, cache)
}

/** This device's mode and this community's last-seen accent. */
export function readStoredTheme() {
  removeLegacyKeys()
  const saved = readJson(STORAGE_KEY) || {}
  return {
    mode: MODES.includes(saved.mode) ? saved.mode : DEFAULT_MODE,
    ...readCachedAccent(),
  }
}

/** Applies `theme` to the document and returns the resolved mode. */
export function applyTheme({ mode, accent, custom }) {
  const root = document.documentElement
  const resolved = resolveMode(mode)

  const vars = accentVariables(accent, custom, resolved)
  Object.entries(vars).forEach(([name, value]) => {
    root.style.setProperty(name, value)
  })
  // Pictures cannot read the variables (src/theme/accentImages.js).
  const isDefault = accent === DEFAULT_ACCENT
  showAccentImages(isDefault ? DEFAULT_ACCENT : `${accent}:${custom ? custom.primary : ''}:${resolved}`, isDefault ? null : vars)
  root.setAttribute('data-mode', resolved)
  root.setAttribute('data-accent', accent)
  document.body.classList.toggle('dark-layout', resolved === 'dark')
  // The header's colour in each mode, so a phone's browser bar matches it.
  const themeColor = document.querySelector('meta[name="theme-color"]')
  if (themeColor) themeColor.setAttribute('content', resolved === 'dark' ? '#12151C' : '#FFFFFF')

  window.dispatchEvent(new CustomEvent(THEME_CHANGED_EVENT, { detail: { mode, accent, resolved } }))
  return resolved
}

/** Calls `callback` when the OS switches between light and dark. */
export function onSystemModeChange(callback) {
  const query = darkQuery()
  if (!query) return
  // Safari before 14 only has the deprecated addListener.
  if (query.addEventListener) query.addEventListener('change', callback)
  else if (query.addListener) query.addListener(callback)
}
